import React from "react";
import { formatDate, formatDateTime, formatMoney } from "../../shared/utils/format";
import { PLAN_STATE_CLASS, PLAN_STATE_LABEL, planState } from "../../shared/utils/subscription";
import { useUserSection } from "./useUserSection";
import { LoadState, SectionCard, StageBadge, StatusBadge } from "./ui";
import type { ContentStage, StatusCounts, SubscriptionRow, UserHeader, UserOverview } from "./types";
import { planTimeline } from "./planTimeline";
import { PRODUCT_LABEL, isProductLine } from "../../config/products";

const sumCounts = (counts: StatusCounts) => Object.values(counts).reduce((a, b) => a + b, 0);

const Stat: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({ label, value, hint }) => (
  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
    <p className="text-2xl font-semibold text-gray-900">{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

const CountList: React.FC<{ counts: StatusCounts; asStages?: boolean }> = ({ counts, asStages }) => {
  const entries = Object.entries(counts);
  if (entries.length === 0) return <p className="text-sm text-gray-400">None yet.</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {entries.map(([status, n]) => (
        <span key={status} className="flex items-center gap-1.5">
          {asStages ? <StageBadge stage={status as ContentStage} /> : <StatusBadge status={status} />}
          <span className="text-sm font-medium text-gray-700">{n}</span>
        </span>
      ))}
    </div>
  );
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
    <span className="text-gray-500 shrink-0">{label}</span>
    <span className="font-medium text-gray-900 text-right break-words min-w-0">{children}</span>
  </div>
);

export const PlanStateBadge: React.FC<{ sub: SubscriptionRow }> = ({ sub }) => {
  const state = planState(sub);
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${PLAN_STATE_CLASS[state]}`}>
      {PLAN_STATE_LABEL[state]}
    </span>
  );
};

const billingText = (sub: SubscriptionRow): string => {
  const productLine = sub.prices?.plans.productLine;
  return [
    isProductLine(productLine) ? PRODUCT_LABEL[productLine] : productLine,
    sub.provider,
    sub.billingPeriod,
    sub.prices?.creditAmount ? `${sub.prices.creditAmount} credits/period` : null,
  ]
    .filter(Boolean)
    .join(" · ");
};

const SubscriptionItem: React.FC<{ sub: SubscriptionRow }> = ({ sub }) => (
  <li className="py-3 first:pt-0 last:pb-0">
    <div className="mb-1 flex items-center justify-between gap-3">
      <span className="text-sm font-semibold text-gray-900">{sub.prices?.plans.name ?? "Unknown plan"}</span>
      <PlanStateBadge sub={sub} />
    </div>
    <Row label="Billing">{billingText(sub)}</Row>
    {planTimeline(sub).map((row) => (
      <Row key={row.label} label={row.label}>
        {row.value}
      </Row>
    ))}
    <Row label="Provider status">{sub.status}</Row>
    {sub.lastPaymentError && (
      <Row label="Last payment error">
        <span className="text-red-600">{sub.lastPaymentError}</span>
      </Row>
    )}
  </li>
);

export function OverviewTab({ userId, header }: Readonly<{ userId: number; header: UserHeader }>) {
  const { data, loading, error, reload } = useUserSection<UserOverview>(userId, "overview");
  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-gray-200">
        <LoadState loading={loading} error={error} onRetry={reload} />
      </div>
    );
  }
  return <OverviewContent overview={data} header={header} />;
}

// The plan trial lives on the subscription; users.freeUntil is the older free-days grant.
const trialText = (subscriptions: SubscriptionRow[]): string => {
  const trial = subscriptions.find((s) => s.status === "trialing" && s.trialEndAt);
  if (!trial) return "No active trial";
  return trial.cancelAtPeriodEnd ? `${formatDate(trial.trialEndAt)} (cancelled)` : formatDate(trial.trialEndAt);
};

function OverviewContent({ overview, header }: Readonly<{ overview: UserOverview; header: UserHeader }>) {
  const { counts, credits } = overview;
  const { user, subscriptions, clients } = header;
  const spend = Object.entries(counts.adSpendByCurrency);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-8">
        <Stat label="Brands" value={counts.clients} />
        <Stat label="Topics" value={counts.topics} />
        <Stat label="Posts" value={counts.posts.total} hint={`${counts.posts.byStatus.published ?? 0} published`} />
        <Stat label="Campaigns" value={sumCounts(counts.campaigns)} hint={`${counts.campaigns.ACTIVE ?? 0} active`} />
        <Stat
          label="Ad spend"
          value={spend.length === 0 ? 0 : spend.map(([cur, amt]) => formatMoney(amt, cur)).join(" + ")}
          hint={spend.some(([cur]) => cur === "UNKNOWN") ? "Currency not set" : undefined}
        />
        <Stat label="Blogs" value={sumCounts(counts.blogs)} />
        <Stat label="Accounts" value={sumCounts(counts.connections)} />
        <Stat label="Credits left" value={credits.available ?? "—"} hint={credits.available === null ? "Balance unavailable" : undefined} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <SectionCard title="Account">
          <Row label="User ID">#{user.id}</Row>
          <Row label="Email">{user.email}</Row>
          <Row label="Verified">{user.verified ? "Yes" : "No"}</Row>
          <Row label="Account">{user.isDeleted === 1 ? "Active" : "Deleted"}</Row>
          <Row label="Country">{user.country || "—"}</Row>
          <Row label="Platform">{user.signupPlatform || "—"}</Row>
          <Row label="Joined">{formatDateTime(user.createdAt)}</Row>
          <Row label="Last login">{formatDateTime(user.lastLoginAt)}</Row>
          <Row label="Trial until">{trialText(subscriptions)}</Row>
          {user.freeUntil && <Row label="Free days until">{formatDate(user.freeUntil)}</Row>}
          <Row label="Lifecycle emails">{user.lifecycleEmailsOptOut ? "Opted out" : "Subscribed"}</Row>
        </SectionCard>

        <SectionCard title="Credits">
          <Row label="Available">{credits.available ?? "Unavailable"}</Row>
          <Row label="Granted (lifetime)">{credits.granted}</Row>
          <Row label="Spent (lifetime)">{credits.spent}</Row>
          <Row label="Expired">{credits.expired}</Row>
          {credits.adjusted !== 0 && <Row label="Adjusted">{credits.adjusted}</Row>}
        </SectionCard>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <SectionCard title="Subscriptions">
          {subscriptions.length === 0 ? (
            <p className="text-sm text-gray-400">No subscriptions.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {subscriptions.map((s) => (
                <SubscriptionItem key={s.id} sub={s} />
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard title="Activity by status">
          <div className="space-y-3">
            {[
              { label: "Posts", counts: counts.posts.byStatus, asStages: true },
              { label: "Campaigns", counts: counts.campaigns },
              { label: "Blogs", counts: counts.blogs },
              { label: "Connected accounts", counts: counts.connections },
              { label: "Lifecycle emails", counts: counts.emails },
            ].map((group) => (
              <div key={group.label}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{group.label}</p>
                <CountList counts={group.counts} asStages={group.asStages} />
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <SectionCard title={`Brands (${clients.length})`}>
        {clients.length === 0 ? (
          <p className="text-sm text-gray-400">No brands yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {clients.map((c) => (
              <span key={c.id} className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm">
                {c.businessName || `Brand #${c.id}`}
                <StatusBadge status={c.status} />
              </span>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
